import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";

import { Turnstile } from "./Turnstile";
import {
  LEAD_RETRY_DELAYS_MS,
  clearLeadDraft,
  createTokenWaiter,
  finalLeadError,
  restoreLeadDraft,
  saveLeadDraft,
  sleep,
} from "./lead-retry";
import {
  clearNorwood,
  rememberLeadId,
  rememberPhone,
  rememberStep1Norwood,
  trackLeadEventUnlessRepeat,
} from "./quick-quote-shared";
import { startQuickQuote } from "@/lib/forms/quick-quote.functions";
import { rememberW2lPreview } from "@/lib/forms/test-preview";
import { getTrackingParams } from "@/lib/utm-tracking";
import { countBannerStep, withBannerVariant } from "@/lib/banner-ab";
import { norwoodClasses } from "@/lib/site-data";

const HEARD_OPTIONS = [
  "Youtube",
  "Past Patient",
  "Yelp",
  "Television",
  "Social Media",
  "Referral",
  "Billboard",
  "Google Search",
  "Radio",
  "Other",
];

// 16px text: anything smaller makes iOS Safari zoom the page on focus.
const inputCls =
  "w-full border border-border bg-background px-3 py-2 text-base text-foreground outline-none focus:border-primary";
const labelCls = "mb-1 block text-sm font-semibold text-foreground";

function splitName(full: string) {
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return { first_name: parts[0], last_name: parts[0] };
  return { first_name: parts.slice(0, -1).join(" "), last_name: parts[parts.length - 1] };
}

/**
 * Quick Quote form — mirrors the form on restorehair.com/contact-us/:
 * Norwood class picker, name/phone/email/zip, referral source, three consent
 * checkboxes and a Turnstile bot check.
 */
export function QuickQuoteForm() {
  const navigate = useNavigate();
  const start = useServerFn(startQuickQuote);
  const [token, setToken] = useState<string | null>(null);
  const [norwood, setNorwood] = useState<string>("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const resetRef = useRef<(() => void) | null>(null);
  const waiterRef = useRef(createTokenWaiter());
  useEffect(() => {
    restoreLeadDraft("qq-embedded", formRef.current);
    countBannerStep("step1_view");
  }, []);

  function onToken(t: string) {
    setToken(t);
    waiterRef.current.onToken(t);
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!token || status === "submitting") return;
    const fd = new FormData(e.currentTarget);
    const { first_name, last_name } = splitName(String(fd.get("full_name") || ""));
    const email = String(fd.get("email") || "").trim();
    const phone = String(fd.get("phone") || "").trim();
    const zip = String(fd.get("zip") || "").trim();
    setStatus("submitting");
    setError(null);
    // A send that fails is retried twice (2 s, then 5 s), each time with a
    // fresh Turnstile token: a token is single-use (14 Oct 2026).
    let currentToken: string | null = token;
    for (let attempt = 0; ; attempt += 1) {
      setToken(null);
      try {
        const res = await start({
          data: {
            turnstile_token: currentToken ?? "",
            first_name,
            last_name,
            email,
            phone,
            zip,
            norwood: norwood || undefined,
            heard_about: String(fd.get("heard") || "").trim() || undefined,
            consent_contact: true,
            consent_terms: true,
            consent_sensitive: true,
            page_path: window.location.pathname,
            referrer: document.referrer || undefined,
            tracking: withBannerVariant(getTrackingParams()),
          },
        });
        rememberW2lPreview(res);
        countBannerStep("step1_submit");
        await trackLeadEventUnlessRepeat(res, { em: email, ph: phone, fn: first_name, ln: last_name, zp: zip });
        rememberLeadId(res.lead_id);
        rememberPhone(phone);
        rememberStep1Norwood(res.lead_id, norwood);
        clearNorwood();
        clearLeadDraft("qq-embedded");
        await navigate({ to: "/quick-quote-page-2", search: { qq: res.lead_id } });
        return;
      } catch (err) {
        saveLeadDraft("qq-embedded", formRef.current);
        const delay = LEAD_RETRY_DELAYS_MS[attempt];
        if (delay !== undefined) {
          setError("Still sending, one moment…");
          await sleep(delay);
          currentToken = await waiterRef.current.next(resetRef.current);
          if (currentToken) continue;
        }
        setError(finalLeadError(err));
        setStatus("error");
        // A fresh token for the visitor's own next tap.
        resetRef.current?.();
        return;
      }
    }
  }

  if (status === "success") {
    return (
      <div className="bg-background p-6 md:p-8">
        <h3 className="text-xl font-extrabold text-foreground">Thanks — your quote is on the way.</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          A RESTORE® specialist will reach out shortly with your personalized quote.
        </p>
      </div>
    );
  }

  return (
    <form ref={formRef} onSubmit={onSubmit} className="bg-background p-6 md:p-8">
      <h3 className="text-xl font-extrabold text-foreground">
        Which picture most closely{" "}
        <span className="font-light italic">resembles your hair loss?</span>
        <span className="text-sm font-normal text-muted-foreground"> (optional)</span>
      </h3>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {norwoodClasses.map((c) => {
          const active = norwood === c.label;
          return (
            <button
              type="button"
              key={c.label}
              onClick={() => setNorwood(c.label)}
              aria-pressed={active}
              className={`border p-1 text-center transition ${
                active ? "border-primary ring-2 ring-primary" : "border-border hover:border-primary"
              }`}
            >
              <img src={c.img} alt={c.label} loading="lazy" className="h-auto w-full" />
              <span className="mt-1 block text-xs font-semibold text-foreground">{c.label}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="full_name">Name *</label>
          <input className={inputCls} id="full_name" name="full_name" type="text" required maxLength={160} autoComplete="name" autoCapitalize="words" enterKeyHint="next" />
        </div>
        <div>
          <label className={labelCls} htmlFor="phone">Phone *</label>
          <input className={inputCls} id="phone" name="phone" type="tel" inputMode="tel" required maxLength={32} autoComplete="tel" enterKeyHint="next" />
        </div>
        <div>
          <label className={labelCls} htmlFor="email">Email *</label>
          <input className={inputCls} id="email" name="email" type="email" inputMode="email" required maxLength={255} autoComplete="email" autoCapitalize="off" autoCorrect="off" spellCheck={false} enterKeyHint="next" />
        </div>
        <div>
          <label className={labelCls} htmlFor="zip">Zip Code *</label>
          <input className={inputCls} id="zip" name="zip" type="text" inputMode="numeric" required maxLength={20} autoComplete="postal-code" enterKeyHint="next" />
        </div>
      </div>

      <div className="mt-4">
        <label className={labelCls} htmlFor="heard">How did you hear about RESTORE?</label>
        <select className={inputCls} id="heard" name="heard" defaultValue="">
          <option value="" disabled>Select one</option>
          {HEARD_OPTIONS.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </div>

      <div className="mt-5 space-y-3 text-xs leading-relaxed text-muted-foreground">
        <Consent id="consent_contact">
          By checking this box and clicking the Get a Quick Quote button below, I consent to receive
          automated marketing, advertising, transactional, or informational phone calls, text
          messages, or emails, potentially involving use of an autodialer or an artificial or
          prerecorded voice, from or on behalf of Restore Holdings LLC (“Restore”) at the phone
          number or email address I provided above. I understand my consent is not a condition of
          purchase. Reply “STOP” to opt-out of text messages.
        </Consent>
        <Consent id="consent_terms">
          By checking this box and clicking the Get a Quick Quote button below, I also agree to abide
          by Restore’s <a className="underline" href="/terms-of-use/">Terms of Use</a> and{" "}
          <a className="underline" href="/privacy-policy/">Privacy Policy</a>, including submitting all
          disputes to binding individual arbitration.
        </Consent>
        <Consent id="consent_sensitive">
          By checking this box and clicking the Get a Quick Quote button below, I consent to
          Restore’s collection and processing of my “sensitive personal information” for the purposes
          described in our <a className="underline" href="/privacy-policy/">Privacy Policy</a>.
        </Consent>
        <p>
          California Residents: See our{" "}
          <a className="underline" href="/privacy-policy/#ccpa-notice">CCPA notice at collection</a>
        </p>
      </div>

      <div className="mt-5">
        <Turnstile lazy appearance="interaction-only" action="quick-quote" resetRef={resetRef} onToken={onToken} onExpired={() => setToken(null)} onError={() => setToken(null)} />
      </div>

      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

      <button
        type="submit"
        disabled={!token || status === "submitting"}
        className="mt-5 w-full bg-primary px-6 py-4 text-sm font-bold uppercase tracking-wider text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {status === "submitting" ? "Sending…" : "Get A Quick Quote"}
      </button>
    </form>
  );
}

function Consent({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <label className="flex gap-2" htmlFor={id}>
      <input id={id} name={id} type="checkbox" required className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{children}</span>
    </label>
  );
}
